/**
 * Form helpers for create/edit assessment views.
 */
import { ASSESSMENT_TYPE_DEFINITIONS } from './assessment-configs';
import type {
  Assessment,
  AssessmentStatus,
  AssessmentType,
  AssessmentTypeDefinition,
  AutoEvaluationBlockEntry,
  AutoEvaluationMoment,
  AutoEvaluationMomentSummary,
  AutoEvaluationPayload,
  AutoEvaluationRating,
  CreateAssessmentDto,
  NamedReportPayload,
} from '@/types/assessment.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import {
  canRoleValidateAssessmentType,
  canRoleWriteAssessmentType,
} from './assessment-table-utils';

/** Per-type create-form copy (subtitle + target / coordinator sections). */
type AssessmentCreateByType = {
  subtitle: string;
  target?: {
    sectionTitle: string;
    label: string;
    placeholder: string;
    helperText: string;
  };
  coordinator?: {
    sectionTitle: string;
    helperText: string;
  };
};

/** {@link Autocomplete} option for a VSC or coordinator. */
export type AssessmentUserOption = { value: string; label: string };
/** Multi-target chip (volunteer type creates one assessment per id). */
export type AssessmentTargetUser = { id: string; label: string };

/** Create-form sections keyed by assessment type — labels come from the role catalog. */
function getAssessmentCreateByTypeMap(): Record<AssessmentType, AssessmentCreateByType> {
  const volunteer = getRoleLabel('volunteer');
  const coordinator = getRoleLabel('coordinator');
  const autoEvalHelper = `Le ${volunteer} complète lui-même sa grille ; son ${coordinator.toLowerCase()} peut également la remplir.`;
  const coordinatorWriter = {
    sectionTitle: `${coordinator} rédacteur`,
    helperText: 'Personne chargée de rédiger le bilan puis de le soumettre.',
  };

  return {
    volunteer: {
      subtitle: 'Renseignez les informations du bilan volontaire',
      target: {
        sectionTitle: `${volunteer} concernés`,
        label: `Ajouter un ${volunteer}`,
        placeholder: `Rechercher un ${volunteer} de cet établissement...`,
        helperText: `Un bilan distinct est créé par ${volunteer} ; chacun complète le sien.`,
      },
    },
    mission_start: {
      subtitle: 'Renseignez les informations du bilan début de mission',
      target: {
        sectionTitle: `${volunteer} concerné`,
        label: volunteer,
        placeholder: `Rechercher un ${volunteer} de cet établissement...`,
        helperText: autoEvalHelper,
      },
    },
    mid_term: {
      subtitle: 'Renseignez les informations du bilan mi-parcours',
      target: {
        sectionTitle: `${volunteer} concerné`,
        label: volunteer,
        placeholder: `Rechercher un ${volunteer} de cet établissement...`,
        helperText: autoEvalHelper,
      },
    },
    mission_end: {
      subtitle: 'Renseignez les informations du bilan fin de mission',
      target: {
        sectionTitle: `${volunteer} concerné`,
        label: volunteer,
        placeholder: `Rechercher un ${volunteer} de cet établissement...`,
        helperText: autoEvalHelper,
      },
    },
    named: {
      subtitle: 'Renseignez les informations du bilan nominatif',
      target: {
        sectionTitle: `${volunteer} concerné`,
        label: volunteer,
        placeholder: `Rechercher un ${volunteer} de cet établissement...`,
        helperText: 'Le bilan porte sur ce volontaire ; il ne le remplit pas lui-même.',
      },
      coordinator: coordinatorWriter,
    },
    intermediate: {
      subtitle: 'Renseignez les informations du bilan intermédiaire (direction des collèges)',
      coordinator: coordinatorWriter,
    },
    other: {
      subtitle: 'Renseignez les informations du bilan',
      coordinator: coordinatorWriter,
    },
  };
}

/** Replaces `{company}` in catalogue labels. Drops the placeholder when the name is unknown. */
export function brandLabel(template: string, companyName: string | null | undefined): string {
  const name = companyName?.trim();
  if (name) return template.replaceAll('{company}', name);
  let out = template
    .replaceAll(' de {company}', '')
    .replaceAll(' {company}', '')
    .replaceAll('{company} - ', '')
    .replaceAll('{company}', '');
  while (out.includes('  ')) out = out.replaceAll('  ', ' ');
  return out.replaceAll(' .', '.').trim();
}

/** Type definition from {@link ASSESSMENT_TYPE_DEFINITIONS}, or undefined when no type yet. */
export function getAssessmentDefinition(
  type: AssessmentType | '',
): AssessmentTypeDefinition | undefined {
  if (!type) return undefined;
  return ASSESSMENT_TYPE_DEFINITIONS[type];
}

/** Create-form copy for the selected type. */
export function getCreateAssessmentByType(type: AssessmentType | '') {
  if (!type) return undefined;
  return getAssessmentCreateByTypeMap()[type];
}

/** `displayName` → first+last → email → id. */
export function formatAssessmentUserLabel(user: {
  id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  return (
    user.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email ||
    user.id
  );
}

/** Map API users to {@link Autocomplete} options. */
export function mapAssessmentUserOptions(
  users: Array<{
    id: string;
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  }>,
): AssessmentUserOption[] {
  return users.map((user) => ({
    value: user.id,
    label: formatAssessmentUserLabel(user),
  }));
}

/** Append a target chip; no-op if the id is empty or already selected. */
export function addAssessmentTargetUser(
  current: AssessmentTargetUser[],
  id: string,
  options: AssessmentUserOption[],
): AssessmentTargetUser[] {
  if (!id || current.some((user) => user.id === id)) return current;
  const option = options.find((item) => item.value === id);
  return [...current, { id, label: option?.label ?? id }];
}

/** Remove one multi-target chip. */
export function removeAssessmentTargetUser(
  current: AssessmentTargetUser[],
  id: string,
): AssessmentTargetUser[] {
  return current.filter((user) => user.id !== id);
}

/** Required fields follow the type definition (establishment / coordinator / target). */
export function isCreateAssessmentValid(params: {
  definition: AssessmentTypeDefinition | undefined;
  establishmentId: string;
  coordinatorUserId: string;
  targetUsers: AssessmentTargetUser[];
  targetUserId: string;
}): boolean {
  const { definition } = params;
  if (!definition) return false;
  if (definition.establishmentRequired && !params.establishmentId) return false;
  if (definition.coordinatorUserRequired && !params.coordinatorUserId) return false;
  if (definition.allowsMultiTarget) return params.targetUsers.length > 0;
  if (definition.targetUserRequired) return Boolean(params.targetUserId);
  return true;
}

/** Single-target id, or undefined for multi-target / no-target types. */
function resolveCreateTargetUserId(
  definition: AssessmentTypeDefinition,
  targetUserId: string,
): string | undefined {
  if (definition.allowsMultiTarget || !definition.targetUserRequired) return undefined;
  return targetUserId;
}

/** Map the create form to the API body; omit fields the type does not use. */
export function buildCreateAssessmentDto(params: {
  type: AssessmentType | '';
  definition: AssessmentTypeDefinition | undefined;
  establishmentId: string;
  targetUserId: string;
  targetUsers: AssessmentTargetUser[];
  coordinatorUserId: string;
}): CreateAssessmentDto {
  const { type, definition } = params;
  if (!type || !definition) {
    throw new Error(getErrorMessage('selectAssessmentType'));
  }
  return {
    type,
    establishmentId: definition.establishmentRequired ? params.establishmentId : undefined,
    targetUserId: resolveCreateTargetUserId(definition, params.targetUserId),
    targetUserIds: definition.allowsMultiTarget
      ? params.targetUsers.map((user) => user.id)
      : undefined,
    coordinatorUserId: definition.coordinatorUserRequired ? params.coordinatorUserId : undefined,
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getAssessmentFormErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Success toast — plural when more than one assessment was created. */
export function formatCreateAssessmentSuccess(createdCount: number): string {
  if (createdCount > 1) return `${createdCount} bilans générés`;
  return 'Bilan généré';
}

/** Collapse-form title: type label, or a generic fallback while loading. */
export function buildEditAssessmentTitle(assessment: Assessment | undefined): string {
  if (!assessment || assessment.type === 'other') return 'Modifier le bilan';
  return `Modifier le ${ASSESSMENT_TYPE_DEFINITIONS[assessment.type].label.toLowerCase()}`;
}

/** Collapse-form subtitle aligned with other edit forms. */
export function buildEditAssessmentSubtitle(assessment: Assessment | undefined): string {
  if (!assessment || assessment.type === 'other') return 'Modifiez les informations du bilan';
  return `Modifiez les informations du ${ASSESSMENT_TYPE_DEFINITIONS[assessment.type].label.toLowerCase()}`;
}

/** Person pickers stay disabled until an establishment is chosen. */
export function scopedPersonPlaceholder(establishmentChosen: boolean, placeholder: string): string {
  if (establishmentChosen) return placeholder;
  return "Choisissez d'abord un établissement";
}

/** Add or drop a string in an array. */
export function toggleInArray(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((item) => item !== value) : [...arr, value];
}

/** Patch one auto-evaluation block (ratings / remark). */
export function updateBlock(
  payload: AutoEvaluationPayload,
  blockId: string,
  changes: Partial<AutoEvaluationBlockEntry>,
): AutoEvaluationPayload {
  const current = payload.blocks?.[blockId];
  return {
    ...payload,
    blocks: {
      ...payload.blocks,
      [blockId]: { ratings: current?.ratings ?? {}, remark: current?.remark ?? '', ...changes },
    },
  };
}

/** Toggle a rating on a block item (same value clears it). */
export function setRating(
  payload: AutoEvaluationPayload,
  blockId: string,
  itemId: string,
  rating: AutoEvaluationRating,
): AutoEvaluationPayload {
  const ratings = payload.blocks?.[blockId]?.ratings ?? {};
  return updateBlock(payload, blockId, {
    ratings: { ...ratings, [itemId]: ratings[itemId] === rating ? null : rating },
  });
}

/** Current-moment payload, or a sibling moment's stored payload. */
export function payloadOf(
  payload: AutoEvaluationPayload,
  currentMoment: AutoEvaluationMoment | undefined,
  siblings: Partial<Record<AutoEvaluationMoment, AutoEvaluationMomentSummary>> | undefined,
  target: AutoEvaluationMoment,
): AutoEvaluationPayload | undefined {
  return target === currentMoment ? payload : siblings?.[target]?.payload;
}

/** Rating for a block item on the current or a sibling moment. */
export function ratingOf(
  payload: AutoEvaluationPayload,
  currentMoment: AutoEvaluationMoment | undefined,
  siblings: Partial<Record<AutoEvaluationMoment, AutoEvaluationMomentSummary>> | undefined,
  target: AutoEvaluationMoment,
  blockId: string,
  itemId: string,
): AutoEvaluationRating | null {
  return (
    payloadOf(payload, currentMoment, siblings, target)?.blocks?.[blockId]?.ratings?.[itemId] ??
    null
  );
}

/** Trimmed remark for a block on the current or a sibling moment. */
export function remarkOf(
  payload: AutoEvaluationPayload,
  currentMoment: AutoEvaluationMoment | undefined,
  siblings: Partial<Record<AutoEvaluationMoment, AutoEvaluationMomentSummary>> | undefined,
  target: AutoEvaluationMoment,
  blockId: string,
): string {
  return (
    payloadOf(payload, currentMoment, siblings, target)?.blocks?.[blockId]?.remark?.trim() ?? ''
  );
}

/** Replace one completed-mission string. */
export function updateNamedReportMission(
  payload: NamedReportPayload,
  index: number,
  value: string,
): NamedReportPayload {
  const missions = [...payload.assessment.completedMissions];
  missions[index] = value;
  return { ...payload, assessment: { ...payload.assessment, completedMissions: missions } };
}

/** Drop one completed-mission row. */
export function removeNamedReportMission(
  payload: NamedReportPayload,
  index: number,
): NamedReportPayload {
  const missions = payload.assessment.completedMissions.filter((_, i) => i !== index);
  return { ...payload, assessment: { ...payload.assessment, completedMissions: missions } };
}

/** Append an empty completed-mission row. */
export function addNamedReportMission(payload: NamedReportPayload): NamedReportPayload {
  return {
    ...payload,
    assessment: {
      ...payload.assessment,
      completedMissions: [...payload.assessment.completedMissions, ''],
    },
  };
}

/** Add or drop a skill id on a named-report skills list. */
export function toggleNamedReportSkill(
  payload: NamedReportPayload,
  field: 'softSkills' | 'technicalSkills' | 'knowledge',
  value: string,
): NamedReportPayload {
  return {
    ...payload,
    skills: {
      ...payload.skills,
      [field]: toggleInArray(payload.skills[field], value),
    },
  };
}

/** Add or drop a completed training id. */
export function toggleNamedReportTraining(
  payload: NamedReportPayload,
  trainingId: string,
): NamedReportPayload {
  return {
    ...payload,
    trainings: {
      ...payload.trainings,
      completed: toggleInArray(payload.trainings.completed, trainingId),
    },
  };
}

/** Set a training date; empty string → `null`. */
export function setNamedReportTrainingDate(
  payload: NamedReportPayload,
  trainingId: string,
  value: string,
): NamedReportPayload {
  return {
    ...payload,
    trainings: {
      ...payload.trainings,
      dates: { ...payload.trainings.dates, [trainingId]: value || null },
    },
  };
}

/** Edit / disable / validate flags for the assessment detail form. */
export function getAssessmentDetailCapabilities(params: {
  presentationMode: 'view' | 'edit';
  status: AssessmentStatus;
  type: AssessmentType;
  canEditRow: boolean;
  canValidate: boolean;
  roleCodes: string[];
}): { editable: boolean; formDisabled: boolean; canValidateUi: boolean } {
  const editable =
    params.presentationMode !== 'view' &&
    params.status !== 'validated' &&
    params.canEditRow &&
    canRoleWriteAssessmentType(params.roleCodes, params.type);
  return {
    editable,
    formDisabled: !editable,
    canValidateUi:
      params.presentationMode !== 'view' &&
      params.canValidate &&
      canRoleValidateAssessmentType(params.roleCodes, params.type) &&
      params.status === 'submitted',
  };
}

/** True when a list-row bilan needs GET `/:id` to load sibling auto-eval moments. */
export function shouldFetchSiblingMoments(assessment: {
  schemaId?: string;
  autoEvaluationMoments?: unknown;
}): boolean {
  return assessment.schemaId === 'bilan_auto_evaluation' && !assessment.autoEvaluationMoments;
}
